/**
 * @component ry-sidenav
 * @category  nav
 * @summary   Persistent vertical app-shell sidenav (ry-ui-only). Always-visible navigation rail that takes layout space — distinct from `<ry-drawer>` which slides in as an overlay. Collapsible (icon-only mode) via `[collapsed]`; supports inverted/light variants; auto-marks the `aria-current="page"` link via `[data-ry-active]` for CSS targeting; auto-injects a `<ry-logo>` brand if no header is authored.
 *
 * @attribute collapsed   boolean   default=false   Icon-only mode (narrow rail). Toggle to collapse without unmounting.
 * @attribute variant     'inverted' | 'light' ?   default="inverted"   Visual variant — pure CSS hook.
 * @attribute width       string?              Override the rail width (e.g. `"20rem"`). Sets `--ry-sidenav-width` CSS custom property. Default width is theme-controlled (~18rem).
 *
 * @property  collapsed   boolean   Live collapsed state. Setting reflects to the `[collapsed]` attribute.
 *
 * @method    toggle()    Flip between collapsed and expanded. Fires `ry:beforetoggle` (cancelable) then `ry:toggle` with `{collapsed}`.
 *
 * @event ry:beforetoggle
 *        detail: { collapsed: boolean, previous: boolean }
 *        fires:  in `toggle()` and the `collapsed` property setter, after the no-op guard (same value), before the `[collapsed]` attribute is written. `collapsed` is the state about to be applied, `previous` the current one. Direct `setAttribute('collapsed')` / `removeAttribute('collapsed')` bypasses this guard — use `toggle()` or the property if you want it vetoable.
 *        cancelable: preventDefault() leaves the rail in its current state; `ry:toggle` does not fire
 *
 * @event ry:toggle
 *        detail: { collapsed: boolean }
 *        fires:  on every transition between collapsed and expanded — whether via `toggle()`, direct attribute mutation, or the property setter. Does NOT fire on initial mount with `[collapsed]` already set.
 *
 * @state     `data-ry-state` of `"collapsed"` or `"expanded"` mirrors the `[collapsed]` attribute. CSS uses this for the width transition.
 *
 * @content   Free-form. Conventional layout:
 *   - `<header>` — brand area. If empty/missing, ry-sidenav auto-injects an `<a class="ry-sidenav__brand"><ry-logo inline-text></ry-logo></a>`.
 *   - `<nav>` — navigation links. Use `<details><summary>` for collapsible groups (native, no JS needed).
 *   - `<footer>` — user info / sign-out / settings.
 *
 * @dom
 *   ry-sidenav[collapsed?][variant?][width?]   data-ry-target="sidenav"   data-ry-state="collapsed|expanded"
 *     > header   (authored or auto-injected with <ry-logo>)
 *     > nav     {authored links + <details> groups}
 *       > a[data-ry-active]   (the one with aria-current="page")
 *     > footer   {authored content}
 *
 * @gotcha  ry-sidenav does NOT slide — it occupies layout space. For an overlay sidebar that appears on demand, use `<ry-drawer>` instead. The two intentionally serve different patterns: sidenav is app-shell chrome, drawer is a transient panel.
 *
 * @gotcha  Brand auto-injection only kicks in when the existing `<header>` is missing OR empty. If you author any non-whitespace content inside `<header>`, the auto-injection is skipped. To opt out entirely, author an empty `<header><span> </span></header>` (a space passes the textContent.trim() check).
 *
 * @gotcha  Active highlight is wired via `aria-current="page"` on a descendant `<a>`. The wrapper finds the FIRST matching anchor and adds `[data-ry-active]` to it; siblings are left alone. To highlight multiple items, set `[data-ry-active]` directly on each.
 *
 * @gotcha  Setup-once for the active marker. Updating `aria-current` after mount does NOT shift the highlight — the marker is applied once. For client-side routing, your router should swap `[data-ry-active]` between links manually.
 *
 * @gotcha  Live attribute updates are honored for `[collapsed]` (with `ry:toggle` event) and `[width]` (CSS variable updates). Updates to `[variant]` reflect in the attribute but are observed only as a CSS hook — the wrapper doesn't react.
 *
 * @example basic
 * ```html
 * <ry-sidenav>
 *   <nav>
 *     <a href="/" aria-current="page"><ry-icon name="home"></ry-icon><span>Dashboard</span></a>
 *     <a href="/settings"><ry-icon name="settings"></ry-icon><span>Settings</span></a>
 *   </nav>
 * </ry-sidenav>
 * ```
 *
 * @example with-brand-and-groups
 * ```html
 * <ry-sidenav>
 *   <header>
 *     <a class="ry-sidenav__brand" href="/"><img src="/logo.svg"> Acme</a>
 *   </header>
 *   <nav>
 *     <a href="/dashboard" aria-current="page">Dashboard</a>
 *     <details open>
 *       <summary>Reports</summary>
 *       <a href="/reports/sales">Sales</a>
 *       <a href="/reports/users">Users</a>
 *     </details>
 *   </nav>
 *   <footer>
 *     <div class="ry-sidenav__user">Signed in as Ryan</div>
 *   </footer>
 * </ry-sidenav>
 * ```
 *
 * @example collapsed
 * ```html
 * <ry-sidenav collapsed>
 *   <nav>
 *     <a href="/" aria-current="page"><ry-icon name="home"></ry-icon><span>Home</span></a>
 *   </nav>
 * </ry-sidenav>
 * ```
 *
 * @example light-variant
 * ```html
 * <ry-sidenav variant="light">
 *   <nav><a href="/">Home</a></nav>
 * </ry-sidenav>
 * ```
 *
 * @tutorial mount-applies-data-ry-target
 * ```js
 * const el = mount('<ry-sidenav><nav><a href="/">x</a></nav></ry-sidenav>');
 * await expectAttr(el, 'data-ry-target', 'sidenav');
 * ```
 *
 * @tutorial state-mirrors-collapsed-attribute
 * ```js
 * const collapsed = mount('<ry-sidenav collapsed><nav><a href="/">x</a></nav></ry-sidenav>');
 * await expectAttr(collapsed, 'data-ry-state', 'collapsed');
 * const expanded = mount('<ry-sidenav><nav><a href="/">x</a></nav></ry-sidenav>');
 * await expectAttr(expanded, 'data-ry-state', 'expanded');
 * ```
 *
 * @tutorial toggle-flips-state-and-fires-ry-toggle
 * ```js
 * const el = mount('<ry-sidenav><nav><a href="/">x</a></nav></ry-sidenav>');
 * const fired = [];
 * el.addEventListener('ry:toggle', (e) => fired.push(e.detail.collapsed));
 * el.toggle();
 * await expectEqual(el.collapsed, true);
 * await expectAttr(el, 'data-ry-state', 'collapsed');
 * await expectEqual(fired[0], true);
 * el.toggle();
 * await expectEqual(el.collapsed, false);
 * await expectEqual(fired[1], false);
 * ```
 *
 * @tutorial beforetoggle-veto
 * ```js
 * // preventDefault() on ry:beforetoggle keeps the rail where it is — the
 * // detail carries the state about to be applied plus the previous one.
 * const el = mount('<ry-sidenav><nav><a href="/">x</a></nav></ry-sidenav>');
 * const toggled = [];
 * el.addEventListener('ry:toggle', () => toggled.push(1));
 * let seen;
 * el.addEventListener('ry:beforetoggle', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * el.toggle();
 * await expectEqual(seen.collapsed, true, 'detail.collapsed is the NEXT state');
 * await expectEqual(seen.previous, false, 'detail.previous is the current state');
 * await expectEqual(el.collapsed, false, 'vetoed toggle leaves it expanded');
 * await expectAttr(el, 'data-ry-state', 'expanded');
 * await expectEqual(toggled.length, 0, 'ry:toggle did not fire');
 * el.toggle();
 * await expectEqual(el.collapsed, true, 'without the veto, toggle() works');
 * await expectEqual(toggled.length, 1, 'ry:toggle fired once');
 * ```
 *
 * @tutorial aria-current-marks-active-link
 * ```js
 * const el = mount(`
 *   <ry-sidenav>
 *     <nav>
 *       <a href="/" aria-current="page" id="active">Home</a>
 *       <a href="/settings">Settings</a>
 *     </nav>
 *   </ry-sidenav>
 * `);
 * const active = el.querySelector('#active');
 * await expectAttr(active, 'data-ry-active', '');
 * const inactive = el.querySelector('a[href="/settings"]');
 * await expectEqual(inactive.hasAttribute('data-ry-active'), false);
 * ```
 *
 * @tutorial no-aria-current-no-active-mark
 * ```js
 * const el = mount(`
 *   <ry-sidenav>
 *     <nav>
 *       <a href="/">Home</a>
 *       <a href="/settings">Settings</a>
 *     </nav>
 *   </ry-sidenav>
 * `);
 * await expectEqual(el.querySelector('a[data-ry-active]'), null);
 * ```
 *
 * @tutorial brand-auto-injection
 * ```js
 * // Wrong assumption: no <header> means no brand area. Empty/missing
 * // <header> triggers auto-injection of <ry-logo inline-text>.
 * const el = mount('<ry-sidenav><nav><a href="/">x</a></nav></ry-sidenav>');
 * const header = el.querySelector('header');
 * await expectEqual(!!header, true, 'header auto-injected');
 * await expectEqual(!!header.querySelector('ry-logo'), true, 'logo auto-injected');
 * ```
 *
 * @tutorial brand-auto-injection-skipped-when-content-present
 * ```js
 * const el = mount(`
 *   <ry-sidenav>
 *     <header><span>Acme</span></header>
 *     <nav><a href="/">x</a></nav>
 *   </ry-sidenav>
 * `);
 * const header = el.querySelector('header');
 * await expectEqual(header.textContent.trim(), 'Acme', 'authored content preserved');
 * await expectEqual(header.querySelector('ry-logo'), null, 'no auto-inject');
 * ```
 *
 * @tutorial width-attribute-applies-css-variable
 * ```js
 * const el = mount('<ry-sidenav width="22rem"><nav><a href="/">x</a></nav></ry-sidenav>');
 * await expectEqual(el.style.getPropertyValue('--ry-sidenav-width'), '22rem');
 * el.setAttribute('width', '30rem');
 * await expectEqual(el.style.getPropertyValue('--ry-sidenav-width'), '30rem', 'live update');
 * el.removeAttribute('width');
 * await expectEqual(el.style.getPropertyValue('--ry-sidenav-width'), '', 'cleared');
 * ```
 *
 * @see ry-drawer
 * @see ry-tabs
 */

import { RyElement } from '../core/ry-element.js';

interface SidenavToggleDetail {
  collapsed: boolean;
}

interface SidenavBeforeToggleDetail extends SidenavToggleDetail {
  previous: boolean;
}

export class RySidenav extends RyElement {
  static get observedAttributes(): string[] {
    return ['collapsed', 'variant', 'width'];
  }

  setup(): void {
    // Mark the element so CSS hooks (data-ry-target) match.
    if (!this.hasAttribute('data-ry-target')) {
      this.setAttribute('data-ry-target', 'sidenav');
    }

    // Mirror collapsed attribute to data-ry-state so CSS can transition.
    this.#applyCollapsed();

    // Auto-mark the active item from aria-current="page" if not already set.
    this.#wireActive();

    // Custom width override (rare; users mostly use the default).
    const w = this.getAttribute('width');
    if (w) this.style.setProperty('--ry-sidenav-width', w);

    // Brand fallback — if header is missing or empty, inject <ry-logo>.
    // Lets a bare <ry-sidenav> pick up the user's design-system logo
    // automatically via the --ry-logo-* tokens.
    this.#ensureBrand();
  }

  #ensureBrand(): void {
    const existing = this.querySelector(':scope > header');
    if (existing && existing.textContent?.trim()) return;
    const header = existing ?? document.createElement('header');
    if (!header.parentNode) this.prepend(header);
    if (!header.querySelector('.ry-sidenav__brand, ry-logo')) {
      header.innerHTML = '<a class="ry-sidenav__brand" href="#"><ry-logo size="28" inline-text></ry-logo></a>';
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (!this.hasAttribute('data-ry-initialized')) return;
    if (oldValue === newValue) return;
    if (name === 'collapsed') {
      this.#applyCollapsed();
      this.emit<SidenavToggleDetail>('toggle', { collapsed: this.collapsed });
    } else if (name === 'width') {
      const w = this.getAttribute('width');
      if (w) this.style.setProperty('--ry-sidenav-width', w);
      else this.style.removeProperty('--ry-sidenav-width');
    }
  }

  #applyCollapsed(): void {
    this.state = this.hasAttribute('collapsed') ? 'collapsed' : 'expanded';
  }

  #wireActive(): void {
    // If any descendant <a> has aria-current="page", flag it for CSS.
    // Skip if the user already set data-ry-state explicitly.
    const current = this.querySelector<HTMLAnchorElement>('a[aria-current="page"]');
    if (current) current.setAttribute('data-ry-active', '');
  }

  /** Public API: toggle / get / set collapsed */
  toggle(): void {
    this.collapsed = !this.collapsed;
  }

  get collapsed(): boolean {
    return this.hasAttribute('collapsed');
  }

  set collapsed(v: boolean) {
    const previous = this.collapsed;
    if (v === previous) return;
    // Interception point for toggle() + the property setter. The attribute
    // write below is what attributeChangedCallback turns into ry:toggle.
    if (!this.emit<SidenavBeforeToggleDetail>('beforetoggle', { collapsed: v, previous })) return;
    if (v) this.setAttribute('collapsed', '');
    else this.removeAttribute('collapsed');
  }
}

customElements.define('ry-sidenav', RySidenav);
